import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getUsuarios, cambiarEstadoUsuario, eliminarUsuario } from '../../services/usuariosService';
import { getCategoriasAdmin } from '../../services/adminService';
import { useAuth } from '../../context/AuthContext';
import { useInstitucion } from '../../context/InstitucionContext';
import { esSuperAdmin, esPublicador, ETIQUETAS_ROL, ROLES } from '../../utils/permisos';

// Usuarios de la institución activa (un SuperAdmin sin institución activa ve los de todas).
const AdminUsuarios = () => {
  const { usuario: actual } = useAuth();
  const { institucion, paleta: colors } = useInstitucion();
  const superAdmin = esSuperAdmin(actual);
  const todas = superAdmin && !institucion;

  const [usuarios, setUsuarios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsuarios(await getUsuarios(todas));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [todas]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Nombres de las categorías raíz para mostrar el alcance de los publicadores.
  useEffect(() => {
    if (!institucion) {
      setCategorias([]);
      return;
    }
    getCategoriasAdmin()
      .then(setCategorias)
      .catch(() => setCategorias([]));
  }, [institucion]);

  const nombresCategorias = useMemo(() => new Map(categorias.map((c) => [c.id, c.nombre])), [categorias]);

  const descripcionAlcance = (u) => {
    if (!esPublicador(u)) return '—';
    if (!u.categoriasRaiz || u.categoriasRaiz.length === 0) return 'Sin categorías asignadas';
    return u.categoriasRaiz.map((id) => nombresCategorias.get(id) || `#${id}`).join(', ');
  };

  const handleToggleEstado = async (u) => {
    try {
      await cambiarEstadoUsuario(u.id, !u.activo);
      setUsuarios((prev) => prev.map((x) => (x.id === u.id ? { ...x, activo: !x.activo } : x)));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEliminar = async (u) => {
    if (!window.confirm(`¿Eliminar el usuario "${u.nombre}" (${u.email})? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await eliminarUsuario(u.id);
      setUsuarios((prev) => prev.filter((x) => x.id !== u.id));
    } catch (err) {
      alert(err.message);
    }
  };

  const formatoFecha = (iso) => {
    if (!iso) return 'Nunca';
    try {
      return new Date(iso).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  const claseRol = (rol) =>
    rol === ROLES.SUPER_ADMIN
      ? 'bg-purple-100 text-purple-700'
      : rol === ROLES.ADMIN_INSTITUCION
        ? 'bg-blue-100 text-blue-700'
        : 'bg-gray-100 text-gray-700';

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
          <p className="text-gray-500 text-sm mt-1">
            {todas
              ? 'Todos los usuarios de la plataforma'
              : `Quiénes administran y publican en ${institucion ? institucion.nombreCorto : 'la institución'}`}
          </p>
        </div>
        <Link
          to="/admin/usuarios/nuevo"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90"
          style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuevo usuario
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Cargando usuarios...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-500">{error}</div>
        ) : usuarios.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No hay usuarios para mostrar.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-3 font-semibold">Usuario</th>
                  <th className="px-4 py-3 font-semibold">Rol</th>
                  {todas && <th className="px-4 py-3 font-semibold hidden md:table-cell">Institución</th>}
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Alcance</th>
                  <th className="px-4 py-3 font-semibold hidden sm:table-cell">Último acceso</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((u) => {
                  const esYo = actual && u.id === actual.id;
                  return (
                    <tr key={u.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {u.nombre} {esYo && <span className="text-xs text-gray-400 font-normal">(tú)</span>}
                        </div>
                        <div className="text-gray-400 text-xs">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${claseRol(u.rol)}`}>
                          {ETIQUETAS_ROL[u.rol] || u.rol}
                        </span>
                      </td>
                      {todas && (
                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{u.institucionNombre || '— plataforma —'}</td>
                      )}
                      <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{descripcionAlcance(u)}</td>
                      <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{formatoFecha(u.ultimoAcceso)}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleEstado(u)}
                          disabled={esYo}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-60 ${
                            u.activo
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                          title={esYo ? 'No puedes cambiar tu propio estado' : 'Clic para cambiar el estado'}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${u.activo ? 'bg-green-500' : 'bg-gray-400'}`} />
                          {u.activo ? 'Activo' : 'Inactivo'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/usuarios/${u.id}/editar`}
                            className="text-gray-500 hover:text-gray-900 p-1.5 rounded-md hover:bg-gray-100"
                            title="Editar"
                          >
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Link>
                          <button
                            onClick={() => handleEliminar(u)}
                            disabled={esYo}
                            className="text-red-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 disabled:opacity-40"
                            title={esYo ? 'No puedes eliminar tu propio usuario' : 'Eliminar'}
                          >
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsuarios;
